import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalVariant } from 'types/Modal';
import { useModalService } from './useModalService';
// import { useBlocker } from 'react-router-dom';

type Props = {
  hasUnsavedChanges: boolean;
  message?: string;
  onSaveAndLeave?: (continueNavigation: () => void) => Promise<void> | void;
  onConfirm?: () => void;
  onCancel?: () => void;
};

export const useUnsavedChanges = ({
  hasUnsavedChanges,
  message = 'Du hast ungespeicherte Änderungen. Möchtest du die Seite wirklich verlassen?',
  onSaveAndLeave,
  onConfirm,
  onCancel,
}: Props): void => {
  const modalService = useModalService();
  const navigate = useNavigate();
  const pendingNavigation = useRef<(() => void) | null>(null);
  const hasInitialState = useRef(false);

  // Global click interceptor as we can't use the router's blocker due to necessary upgrade to V7
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent): void => {
      if (!hasUnsavedChanges) return;
      let target = e.target as HTMLElement | null;
      while (target && target.tagName !== 'A') {
        target = target.parentElement;
      }
      if (!target) return;
      const anchor = target as HTMLAnchorElement;
      const href = anchor.getAttribute('href');
      // Only intercept internal links (not external, not download, not target="_blank")
      if (
        href &&
        href.startsWith('/') &&
        !anchor.target &&
        !anchor.hasAttribute('download') &&
        !anchor.getAttribute('rel')?.includes('external')
      ) {
        e.preventDefault();
        e.stopPropagation();
        pendingNavigation.current = () => {
          navigate(href);
        };
        modalService.show(ModalVariant.UNSAVED_CHANGES, {
          message,
          onSaveAndLeave: async () => {
            const continueNavigation = (): void => {
              if (pendingNavigation.current) {
                pendingNavigation.current();
                pendingNavigation.current = null;
              }
            };
            try {
              if (onSaveAndLeave) await onSaveAndLeave(continueNavigation);
              else continueNavigation();
            } catch (error) {
              // Keep user on current page if save-before-leave flow fails.
            }
          },
          onConfirm: () => {
            if (onConfirm) onConfirm();
            if (pendingNavigation.current) {
              pendingNavigation.current();
              pendingNavigation.current = null;
            }
          },
          onCancel: () => {
            if (onCancel) onCancel();
            pendingNavigation.current = null;
          },
        });
      }
    };
    if (hasUnsavedChanges) {
      document.addEventListener('click', handleGlobalClick, true);
    }
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [hasUnsavedChanges, message, modalService, onConfirm, onCancel, navigate, onSaveAndLeave]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = message; // Browser-specific requirement
      }
    };

    const handlePopState = (e: PopStateEvent): void => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        pendingNavigation.current = () => {
          window.history.back();
        };
        modalService.show(ModalVariant.UNSAVED_CHANGES, {
          message,
          onSaveAndLeave: async () => {
            const continueNavigation = (): void => {
              if (pendingNavigation.current) {
                pendingNavigation.current();
                pendingNavigation.current = null;
              }
            };
            try {
              if (onSaveAndLeave) await onSaveAndLeave(continueNavigation);
              else continueNavigation();
            } catch (error) {
              // Keep user on current page if save-before-leave flow fails.
            }
          },
          onConfirm: () => {
            if (onConfirm) onConfirm();
            if (pendingNavigation.current) {
              pendingNavigation.current();
              pendingNavigation.current = null;
            }
          },
          onCancel: () => {
            if (onCancel) onCancel();
            pendingNavigation.current = null;
            window.history.pushState(null, '', window.location.href);
          },
        });
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      if (!hasInitialState.current) {
        window.history.pushState(null, '', window.location.href);
        hasInitialState.current = true;
      }
    } else {
      hasInitialState.current = false;
      pendingNavigation.current = null;
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, message, modalService, onConfirm, onCancel, onSaveAndLeave]);

  useEffect(() => {
    return () => {
      hasInitialState.current = false;
      pendingNavigation.current = null;
    };
  }, []);
};
